import Phaser from 'phaser'
import OfficeScene from './scenes/OfficeScene.js'
import { createAgents } from './agents.js'
import { initPanels, selectAgent, deselectAgent, showStatCard, addFeedEntry, updateAgentDot, updatePipelineCounts } from './panels.js'
import { initRealtime, getStats } from './realtime.js'

// ─── Phaser Game Config ───────────────────────────────────────
// Fixed internal resolution — Phaser CSS-scales to fit the browser.
// Sprite pixel coordinates never change regardless of window size.
const GAME_W = 1920
const GAME_H = 1080

const config = {
  type: Phaser.AUTO,
  width: GAME_W,
  height: GAME_H,
  parent: 'game-container',
  backgroundColor: '#1a1a2e',
  pixelArt: true,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    expandParent: true,
  },
  scene: [OfficeScene],
}

const game = new Phaser.Game(config)

// ─── Wait for Scene Ready ─────────────────────────────────────
game.events.once('scene-ready', (scene) => {
  const agentCtx = createAgents(scene)

  // ── Panels ────────────────────────────────────────────────────
  initPanels((agentName) => {
    if (agentName) {
      const stats = getStats(agentName)
      showStatCard(agentName, stats)
    } else {
      // deselect handled by panels.js internally
    }
  })

  // Wire stat-card close button
  const closeBtn = document.getElementById('stat-card-close')
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      deselectAgent()
    })
  }

  // ── Sprite Click → Agent Select ───────────────────────────────
  for (const [name, agent] of Object.entries(agentCtx.agents)) {
    agent.sprite.on('pointerdown', () => {
      selectAgent(name)
    })
  }

  // ── Realtime ──────────────────────────────────────────────────
  initRealtime(agentCtx)

  // ── Demo Mode (D key) ──────────────────────────────────────────
  // Plays through a scripted sequence showcasing every animation.
  let demoRunning = false

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'd' && e.key !== 'D') return
    if (document.activeElement?.tagName === 'INPUT') return
    if (demoRunning) return
    demoRunning = true
    runDemo(agentCtx)
  })
})

// ─── Demo Sequence ──────────────────────────────────────────────
function runDemo(ctx) {
  const { processEvent, showSpeechBubble, setState, moveAllToRoundtable } = ctx
  const now = () => new Date().toISOString()

  // Banner
  const banner = document.createElement('div')
  banner.id = 'demo-banner'
  banner.style.cssText = [
    'position: fixed', 'top: 54px', 'left: 50%', 'transform: translateX(-50%)',
    'background: rgba(255, 215, 0, 0.15)', 'border: 1px solid rgba(255, 215, 0, 0.5)',
    'color: #ffd700', 'padding: 6px 20px', 'border-radius: 6px', 'z-index: 9999',
    'font-family: "Orbitron", monospace', 'font-size: 12px', 'letter-spacing: 2px',
  ].join(';')
  banner.textContent = 'DEMO MODE'
  document.body.appendChild(banner)

  // Step label (updates below the banner)
  const stepLabel = document.createElement('div')
  stepLabel.style.cssText = [
    'position: fixed', 'top: 86px', 'left: 50%', 'transform: translateX(-50%)',
    'background: rgba(0,0,0,0.85)', 'color: #00f5ff', 'padding: 4px 14px',
    'border-radius: 4px', 'z-index: 9999', 'font-family: monospace', 'font-size: 11px',
  ].join(';')
  document.body.appendChild(stepLabel)

  function step(label) {
    stepLabel.textContent = label
    console.log(`[Demo] ${label}`)
  }

  // Schedule each demo step with delays
  const steps = [
    // ── 1. Atlas starts morning roundtable ──────────────────────
    [0, () => {
      step('1/12  Atlas calls morning roundtable')
      processEvent('atlas', 'morning_roundtable')
      updateAgentDot('atlas', 'talking')
      addFeedEntry('atlas', 'Morning all. Pipeline is bone dry — zero pending, zero ready, zero posts. Today is about proving the system.', now(), 'morning_roundtable')
    }],

    // ── 2. Muse joins roundtable ────────────────────────────────
    [3000, () => {
      step('2/12  Muse joins roundtable discussion')
      setState('muse', 'talking')
      updateAgentDot('muse', 'talking')
      showSpeechBubble('muse', 'Day 3 — lets get content moving. Ava, Chloe, Jade are strong first picks.')
      addFeedEntry('muse', 'Day 3 — lets get content moving. Ava, Chloe, Jade are strong first picks. I have all 5 proposals queued and will brief the team.', now(), 'morning_roundtable')
    }],

    // ── 3. Lens joins ───────────────────────────────────────────
    [5500, () => {
      step('3/12  Lens shares analytics')
      setState('lens', 'talking')
      updateAgentDot('lens', 'talking')
      showSpeechBubble('lens', 'All 10 personas at 8 posts, 0 followers. Need content to measure.')
      addFeedEntry('lens', 'Day 3 baseline confirmed: All 10 personas at 8 posts, 0 followers. No performance data available yet — need content in pipeline.', now(), 'morning_roundtable')
    }],

    // ── 4. Roundtable ends, agents return to desks ──────────────
    [9000, () => {
      step('4/12  Roundtable ends — agents return to desks')
      processEvent('atlas', 'heartbeat_complete')
      processEvent('muse', 'workflow_run')
      processEvent('lens', 'heartbeat_complete')
      processEvent('pixel', 'heartbeat_complete')
      processEvent('echo', 'heartbeat_complete')
      updatePipelineCounts({ pending: 3, generating: 0, ready: 0, posted: 5 })
    }],

    // ── 5. Muse creates content brief ───────────────────────────
    [13000, () => {
      step('5/12  Muse creates content brief → walks to Pixel')
      processEvent('muse', 'content_brief_created')
      updateAgentDot('muse', 'working')
      showSpeechBubble('muse', 'Ava brief ready — "Missing him at 2am" hook')
      addFeedEntry('muse', 'Content brief created for Ava: "my friends dont get why i stare at his hoodie" — 5 slides, LDR vibe', now(), 'content_brief_created')
      updatePipelineCounts({ pending: 2, generating: 1, ready: 0, posted: 5 })
    }],

    // ── 6. Pixel starts generating ──────────────────────────────
    [17000, () => {
      step('6/12  Pixel generating images (working state)')
      setState('pixel', 'working')
      updateAgentDot('pixel', 'working')
      showSpeechBubble('pixel', 'Generating Ava slideshow... 5 slides with LoRA')
      addFeedEntry('pixel', 'Generating slideshow for Ava: 5 slides, LDR style profile, selfie LoRA overlay', now(), 'workflow_run')
    }],

    // ── 7. Pixel finishes — walks to Echo ───────────────────────
    [22000, () => {
      step('7/12  Pixel done! Celebrates → walks to Echo')
      processEvent('pixel', 'content_ready')
      updateAgentDot('pixel', 'celebrating')
      showSpeechBubble('pixel', 'Ava slides ready! Handing off to Echo.')
      addFeedEntry('pixel', 'Visual pipeline complete for Ava — 5 slides rendered, all LoRAs applied, ready for posting', now(), 'content_ready')
      updatePipelineCounts({ pending: 2, generating: 0, ready: 1, posted: 5 })
    }],

    // ── 8. Echo picks up and posts ──────────────────────────────
    [26000, () => {
      step('8/12  Echo posting to TikTok (working state)')
      processEvent('echo', 'workflow_run')
      updateAgentDot('echo', 'working')
      showSpeechBubble('echo', 'Posting Ava carousel... uploading 5 images to device')
      addFeedEntry('echo', 'Posting Ava carousel: uploading images via curl, navigating TikTok UI', now(), 'workflow_run')
    }],

    // ── 9. Post successful! ─────────────────────────────────────
    [31000, () => {
      step('9/12  Post successful! Echo celebrates')
      processEvent('echo', 'content_posted')
      updateAgentDot('echo', 'celebrating')
      showSpeechBubble('echo', 'Posted! Ava carousel is live on TikTok')
      addFeedEntry('echo', 'Successfully posted Ava carousel to TikTok — 5 slides, caption + hashtags applied', now(), 'content_posted')
      updatePipelineCounts({ pending: 2, generating: 0, ready: 0, posted: 6 })
    }],

    // ── 10. A failure scenario ──────────────────────────────────
    [35000, () => {
      step('10/12  Simulating failure — Pixel frustrated')
      setState('pixel', 'frustrated')
      updateAgentDot('pixel', 'frustrated')
      showSpeechBubble('pixel', 'Chloe LoRA failed — image service timeout')
      addFeedEntry('pixel', 'Image generation failed for Chloe: LoRA service returned timeout after 60s', now(), 'content_generation_failed')
    }],

    // ── 11. Lens runs analytics ─────────────────────────────────
    [39000, () => {
      step('11/12  Lens runs daily analytics')
      setState('lens', 'working')
      updateAgentDot('lens', 'working')
      showSpeechBubble('lens', 'Scraping analytics for all 10 personas...')
      addFeedEntry('lens', 'Daily analytics run: scraping views, likes, and follower counts across all 10 persona accounts', now(), 'daily_report')
    }],

    // ── 12. All agents return to desks — demo complete ─────────
    [43000, () => {
      step('12/12  Demo complete — everyone returns to desks')
      for (const name of ['atlas', 'muse', 'pixel', 'echo', 'lens']) {
        processEvent(name, 'heartbeat_complete')  // triggers moveToStation + idle state
        updateAgentDot(name, 'idle')
      }
      // Clean up banner after agents finish walking
      setTimeout(() => {
        banner.remove()
        stepLabel.remove()
        demoRunning = false
      }, 3000)
    }],
  ]

  for (const [delay, fn] of steps) {
    setTimeout(fn, delay)
  }
}
